import * as React from "react";
import { cn } from "@/lib/utils";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** "raised" = surface + border + shadow; "flat" = surface + border only;
   *  "ghost" = transparent border-only */
  variant?: "raised" | "flat" | "ghost";
  interactive?: boolean;
}

function Card({ className, variant = "flat", interactive, ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      className={cn(
        "flex flex-col rounded-lg text-fg",
        variant === "raised" && "bg-surface border border-border-subtle shadow-sm",
        variant === "flat"   && "bg-surface border border-border",
        variant === "ghost"  && "bg-transparent border border-border-subtle",
        interactive &&
          "cursor-pointer transition-all duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:border-border-strong hover:shadow-sm",
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "flex items-start justify-between gap-3 px-4 pt-4 pb-3",
        "[&:has([data-slot=card-action])]:grid [&:has([data-slot=card-action])]:grid-cols-[1fr_auto]",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="card-title"
      className={cn("text-[15px] font-semibold leading-tight tracking-tight", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-xs text-fg-muted mt-0.5", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div data-slot="card-action" className={cn("flex items-center gap-1", className)} {...props} />;
}

function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div data-slot="card-content" className={cn("px-4 pb-4", className)} {...props} />;
}

function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-4 py-3 border-t border-border-subtle", className)}
      {...props}
    />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
